import React, { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../config/supabase'
import { useNavigate } from 'react-router-dom'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { motion } from 'framer-motion'
import AddChild from './AddChild'

const { FiUsers, FiTrendingUp, FiPlus, FiBook, FiCalendar, FiEye } = FiIcons

const ParentDashboard = () => {
  const { userProfile } = useAuth()
  const navigate = useNavigate()
  const [children, setChildren] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddChild, setShowAddChild] = useState(false)

  useEffect(() => {
    if (userProfile) {
      fetchChildren()
    }
  }, [userProfile])

  const fetchChildren = async () => {
    try {
      setLoading(true)

      // Get all children linked to this parent
      const { data: childrenData, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('parent_id', userProfile.id)
        .eq('role', 'student')

      if (error) throw error

      // For each child, fetch their progress
      const childrenWithProgress = await Promise.all(
        (childrenData || []).map(async (child) => {
          // Get completed lessons count
          const { data: completedLessons } = await supabase
            .from('lesson_progress')
            .select('lesson_id')
            .eq('student_id', child.user_id)
            .eq('completed', true)

          // Get total lessons (assuming 30 days curriculum)
          const totalLessons = 30

          // Get current day (first incomplete day)
          let currentDay = 1
          const { data: allLessons } = await supabase
            .from('lessons')
            .select('id, day_number')
            .order('day_number')

          if (allLessons && completedLessons) {
            const completedIds = completedLessons.map(cl => cl.lesson_id)

            for (let day = 1; day <= 30; day++) {
              const dayLessons = allLessons.filter(l => l.day_number === day)
              const hasIncomplete = dayLessons.some(lesson => !completedIds.includes(lesson.id))

              if (hasIncomplete) {
                currentDay = day
                break
              }
            }
          }

          return {
            ...child,
            completedLessons: completedLessons?.length || 0,
            totalLessons,
            currentDay,
            progressPercent: Math.round(((completedLessons?.length || 0) / totalLessons) * 100)
          }
        })
      )

      setChildren(childrenWithProgress)
    } catch (error) {
      console.error('Error fetching children:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleViewProgress = (childId) => {
    navigate(`/child-progress/${childId}`)
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-[#00AAFF] to-[#1B4C87] rounded-2xl p-8 text-white mb-8"
      >
        <h1 className="text-3xl font-bold mb-2">
          Welcome, {userProfile?.first_name}! 👨‍👩‍👧‍👦
        </h1>
        <p className="text-blue-100 text-lg">
          Monitor your children's learning progress and support their educational journey
        </p>
      </motion.div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Total Children</p>
              <p className="text-3xl font-bold text-gray-800 mt-1">{children.length}</p>
            </div>
            <div className="w-12 h-12 bg-[#00AAFF]/10 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiUsers} className="w-6 h-6 text-[#00AAFF]" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Active Learners</p>
              <p className="text-3xl font-bold text-gray-800 mt-1">
                {children.filter(c => c.currentDay > 1).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Avg. Progress</p>
              <p className="text-3xl font-bold text-gray-800 mt-1">
                {children.length > 0
                  ? Math.round(children.reduce((sum, c) => sum + c.progressPercent, 0) / children.length)
                  : 0}%
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiBook} className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Children List */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center">
            <SafeIcon icon={FiUsers} className="w-6 h-6 mr-2 text-[#00AAFF]" />
            My Children
          </h2>
          <button
            onClick={() => setShowAddChild(true)}
            className="flex items-center px-4 py-2 bg-[#00AAFF] text-white rounded-lg hover:bg-[#0099E5] transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-5 h-5 mr-2" />
            Add Child
          </button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse bg-gray-200 h-32 rounded-lg" />
            ))}
          </div>
        ) : children.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {children.map((child, index) => (
              <motion.div
                key={child.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 * index }}
                className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#00AAFF] transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">
                      {child.first_name} {child.last_name}
                    </h3>
                    <p className="text-sm text-gray-600">{child.email}</p>
                  </div>
                  <div className="w-10 h-10 bg-[#00AAFF]/10 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-[#00AAFF]">
                      {child.first_name.charAt(0)}
                    </span>
                  </div>
                </div>

                {/* Progress Circle */}
                <div className="flex items-center justify-center mb-4">
                  <div className="relative w-20 h-20">
                    <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 36 36">
                      <path
                        className="text-gray-200"
                        stroke="currentColor"
                        strokeWidth="3"
                        fill="transparent"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <path
                        className="text-[#00AAFF]"
                        stroke="currentColor"
                        strokeWidth="3"
                        fill="transparent"
                        strokeDasharray={`${child.progressPercent}, 100`}
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-sm font-bold text-gray-800">
                        {child.progressPercent}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2" />
                    <span>Day {child.currentDay} of 30</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <SafeIcon icon={FiBook} className="w-4 h-4 mr-2" />
                    <span>{child.completedLessons} lessons completed</span>
                  </div>
                </div>

                {/* View Progress Button */}
                <button
                  onClick={() => handleViewProgress(child.user_id)}
                  className="w-full flex items-center justify-center px-4 py-2 bg-[#00AAFF] text-white rounded-lg hover:bg-[#0099E5] transition-colors"
                >
                  <SafeIcon icon={FiEye} className="w-4 h-4 mr-2" />
                  View Details
                </button>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <SafeIcon icon={FiUsers} className="w-20 h-20 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No Children Added Yet</h3>
            <p className="text-gray-500 mb-6">
              Add your first child to start monitoring their progress
            </p>
            <button
              onClick={() => setShowAddChild(true)}
              className="inline-flex items-center px-6 py-3 bg-[#00AAFF] text-white rounded-lg hover:bg-[#0099E5] transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-5 h-5 mr-2" />
              Add Your First Child
            </button>
          </div>
        )}
      </div>

      {/* Add Child Modal */}
      <AddChild
        isOpen={showAddChild}
        onClose={() => setShowAddChild(false)}
        onChildAdded={fetchChildren}
      />
    </div>
  )
}

export default ParentDashboard
